import React, { useRef, useState, useEffect } from 'react';
import {
  FileText,
  Building,
  Calendar,
  User,
  DollarSign,
  Printer
} from 'lucide-react';
import { getRefundInstallments } from '../api';

const CancelPlotSlip = ({ cancelPlot, companyInfo }) => {
  const printRef = useRef(null);
  const [refundInstallments, setRefundInstallments] = useState([]);
  const [loadingInstallments, setLoadingInstallments] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Fetch refund installments if refund type is installment
  // Must be called before any early returns (React Hooks rule)
  useEffect(() => {
    if (cancelPlot?.refund_type === 'installment' && cancelPlot?._id) {
      setLoadingInstallments(true);
      getRefundInstallments(cancelPlot._id)
        .then((response) => {
          if (response && response.refundInstallments) {
            setRefundInstallments(response.refundInstallments || []);
          }
        })
        .catch((error) => {
          console.error('Error fetching refund installments:', error);
          setRefundInstallments([]);
        })
        .finally(() => {
          setLoadingInstallments(false);
        });
    } else {
      // Reset installments if not installment refund or cancelPlot is null
      setRefundInstallments([]);
    }
  }, [cancelPlot?._id, cancelPlot?.refund_type]);

  if (!cancelPlot) {
    return (
      <div className='flex justify-center items-center h-full p-8'>
        <div className='text-center'>
          <p className='text-red-400 mb-2 text-lg'>Cancel plot data not available</p>
        </div>
      </div>
    );
  }

  const formatCurrency = amount => {
    return `Rs ${amount?.toLocaleString('en-PK') || '0'}`;
  };

  const formatDate = date => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCNIC = cnic => {
    if (!cnic) return 'N/A';
    const cleaned = cnic.toString().replace(/\D/g, '');
    if (cleaned.length === 13) {
      return cleaned.replace(/^(\d{5})(\d{7})(\d{1})$/, '$1-$2-$3');
    }
    return cnic;
  };

  const getCustomerDetails = () => {
    if (cancelPlot.customer_details) {
      return cancelPlot.customer_details;
    }
    if (cancelPlot.original_booking?.customer_id && typeof cancelPlot.original_booking.customer_id === 'object') {
      return cancelPlot.original_booking.customer_id;
    }
    return {
      name: 'Unknown Customer',
      contactInfo: {
        phone: 'N/A',
        email: 'N/A',
        cnic: 'N/A',
        address: 'N/A'
      }
    };
  };

  const customer = getCustomerDetails();
  const plot = cancelPlot.plot_id || {};
  
  // Get society data from backend with multiple fallbacks
  // Priority: cancelPlot.society > plot_id.society_id > companyInfo
  const societyFromCancelPlot = cancelPlot.society || null;
  const societyFromPlot = plot?.society_id || null;
  const societyData = societyFromCancelPlot || societyFromPlot || null;
  
  const societyImage = societyData?.society_image || companyInfo?.society_image || companyInfo?.soc?.society_image || 'https://via.placeholder.com/100';
  const societyName = societyData?.name || companyInfo?.name || companyInfo?.soc?.name || 'Society Name';
  const societyLocation = societyData?.location || societyData?.address || companyInfo?.location || companyInfo?.address || '';

  // Pagination for installments
  const totalPages = Math.ceil(refundInstallments.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentInstallments = refundInstallments.slice(startIndex, endIndex);

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    const content = printRef.current.innerHTML;
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Plot Cancellation Slip - ${plot.plot_number || 'N/A'}</title>
          <style>
            @page { 
              size: A4 portrait; 
              margin: 15mm; 
            }
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            body { 
              font-family: 'Segoe UI', Arial, sans-serif; 
              color: #1a1a1a;
              line-height: 1.5;
            }
            .print-header {
              background: #0d9488;
              color: white;
              padding: 20px;
              border-radius: 8px;
              margin-bottom: 24px;
              display: flex;
              justify-content: space-between;
              align-items: center;
            }
            .header-left {
              display: flex;
              align-items: center;
              gap: 15px;
            }
            .society-logo {
              width: 60px;
              height: 60px;
              border-radius: 8px;
              border: 2px solid rgba(255,255,255,0.3);
            }
            .header-title h1 {
              font-size: 22px;
              font-weight: 700;
              margin-bottom: 4px;
            }
            .header-title p {
              font-size: 14px;
              opacity: 0.95;
            }
            .header-right {
              text-align: right;
              background: rgba(255,255,255,0.15);
              padding: 12px 16px;
              border-radius: 6px;
            }
            .header-right p:first-child {
              font-size: 11px;
              opacity: 0.9;
              margin-bottom: 4px;
            }
            .header-right p:last-child {
              font-size: 14px;
              font-weight: 600;
            }
            .info-grid {
              display: grid;
              grid-template-columns: repeat(3, 1fr);
              gap: 16px;
              margin-bottom: 24px;
            }
            .info-card {
              background: #f8f9fa;
              border: 1px solid #e0e0e0;
              border-radius: 8px;
              padding: 16px;
            }
            .info-card-title {
              display: flex;
              align-items: center;
              gap: 8px;
              font-size: 14px;
              font-weight: 600;
              color: #0d9488;
              margin-bottom: 12px;
              padding-bottom: 8px;
              border-bottom: 2px solid #0d9488;
            }
            .info-item {
              margin-bottom: 10px;
            }
            .info-item:last-child {
              margin-bottom: 0;
            }
            .info-label {
              font-size: 10px;
              color: #666;
              font-weight: 600;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              margin-bottom: 4px;
            }
            .info-value {
              font-size: 13px;
              color: #1a1a1a;
              font-weight: 600;
            }
            .status-badge {
              display: inline-block;
              padding: 4px 12px;
              border-radius: 12px;
              font-size: 11px;
              font-weight: 700;
              text-transform: uppercase;
            }
            .status-completed {
              background: #d1fae5;
              color: #065f46;
              border: 1px solid #10b981;
            }
            .status-pending {
              background: #fef3c7;
              color: #92400e;
              border: 1px solid #f59e0b;
            }
            .refund-section {
              background: #f0fdfa;
              border: 2px solid #5eead4;
              border-radius: 8px;
              padding: 20px;
              margin-bottom: 24px;
            }
            .refund-title {
              display: flex;
              align-items: center;
              gap: 8px;
              font-size: 15px;
              font-weight: 700;
              color: #0d9488;
              margin-bottom: 16px;
              padding-bottom: 10px;
              border-bottom: 2px solid #0d9488;
            }
            .refund-item {
              display: flex;
              justify-content: space-between;
              padding: 10px 0;
              font-size: 13px;
              border-bottom: 1px dashed #99f6e4;
            }
            .refund-item:last-child {
              border-bottom: none;
              border-top: 2px solid #0d9488;
              padding-top: 12px;
              margin-top: 8px;
              font-weight: 700;
              font-size: 15px;
              color: #0d9488;
            }
            .refund-label {
              color: #0f766e;
              font-weight: 600;
            }
            .refund-value {
              font-weight: 700;
              color: #134e4a;
            }
            .refund-negative {
              color: #dc2626;
            }
            .signature-section {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 20px;
              margin-bottom: 24px;
            }
            .signature-box {
              border: 1px solid #e0e0e0;
              border-radius: 8px;
              padding: 16px;
              text-align: center;
              background: #fafafa;
            }
            .signature-label {
              font-size: 12px;
              font-weight: 600;
              color: #0d9488;
              margin-bottom: 50px;
            }
            .signature-line {
              border-top: 2px solid #0d9488;
              margin-bottom: 8px;
            }
            .signature-name {
              font-size: 11px;
              color: #666;
            }
            .footer {
              text-align: center;
              font-size: 10px;
              color: #666;
              border-top: 1px solid #e0e0e0;
              padding-top: 16px;
            }
            .footer p {
              margin: 4px 0;
            }
            .installments-table {
              width: 100%;
              border-collapse: collapse;
              margin: 20px 0;
              background: white;
              border-radius: 8px;
              overflow: hidden;
              box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            }
            .installments-table thead {
              background: linear-gradient(135deg, #0d9488 0%, #14b8a6 100%);
              color: white;
            }
            .installments-table th {
              padding: 12px;
              text-align: left;
              font-size: 11px;
              font-weight: 700;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }
            .installments-table td {
              padding: 10px 12px;
              border-bottom: 1px solid #e5e7eb;
              font-size: 12px;
            }
            .installments-table tbody tr:last-child td {
              border-bottom: none;
            }
            .installments-table tbody tr:hover {
              background: #f9fafb;
            }
            .status-paid {
              background: #d1fae5;
              color: #065f46;
              padding: 4px 8px;
              border-radius: 4px;
              font-size: 10px;
              font-weight: 600;
            }
            .status-pending {
              background: #fef3c7;
              color: #92400e;
              padding: 4px 8px;
              border-radius: 4px;
              font-size: 10px;
              font-weight: 600;
            }
            .pagination-info {
              text-align: center;
              font-size: 11px;
              color: #6b7280;
              margin-top: 10px;
              padding: 8px;
              background: #f9fafb;
              border-radius: 4px;
            }
            .no-print { display: none !important; }
            @media print {
              .no-print { display: none !important; }
              .page-break { page-break-after: always; }
            }
          </style>
        </head>
        <body>
          ${content}
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  // Generate installments table HTML for print
  const generateInstallmentsTableHTML = (installments, pageNum, totalPages) => {
    if (!installments || installments.length === 0) return '';
    
    const startIndex = (pageNum - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const pageInstallments = installments.slice(startIndex, endIndex);
    
    let html = `
      <div style="margin-top: 24px; page-break-inside: avoid;">
        <div style="background: linear-gradient(135deg, #0d9488 0%, #14b8a6 100%); color: white; padding: 12px 16px; border-radius: 8px 8px 0 0;">
          <h3 style="margin: 0; font-size: 14px; font-weight: 700;">Refund Installments Schedule</h3>
        </div>
        <table class="installments-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Amount</th>
              <th>Due Date</th>
              <th>Status</th>
              <th>Payment Date</th>
            </tr>
          </thead>
          <tbody>
    `;
    
    pageInstallments.forEach((inst, idx) => {
      const installmentNum = startIndex + idx + 1;
      const isPaid = inst.status === 'paid' || inst.is_paid;
      html += `
        <tr>
          <td style="font-weight: 600;">${installmentNum}</td>
          <td style="font-weight: 700; color: #0d9488;">${formatCurrency(inst.amount)}</td>
          <td>${formatDate(inst.due_date)}</td>
          <td>
            <span class="${isPaid ? 'status-paid' : 'status-pending'}">
              ${isPaid ? 'Paid' : 'Pending'}
            </span>
          </td>
          <td>${inst.payment_date ? formatDate(inst.payment_date) : '—'}</td>
        </tr>
      `;
    });
    
    html += `
          </tbody>
        </table>
        <div class="pagination-info">
          Page ${pageNum} of ${totalPages} | Showing ${pageInstallments.length} of ${installments.length} installments
        </div>
      </div>
    `;
    
    return html;
  };

  return (
    <>
      <div ref={printRef} className='print-content'>
        {/* Header */}
        <div className='print-header' style={{
          background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)',
          color: 'white',
          padding: '20px',
          borderRadius: '8px',
          marginBottom: '24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div className='header-left' style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <img 
              src={societyImage} 
              alt='Society' 
              className='society-logo'
              style={{
                width: '60px',
                height: '60px',
                borderRadius: '8px',
                border: '2px solid rgba(255,255,255,0.3)',
                objectFit: 'cover'
              }}
              onError={(e) => {
                e.target.src = 'https://via.placeholder.com/100';
              }}
            />
            <div className='header-title'>
              <h1 style={{ fontSize: '22px', fontWeight: '700', marginBottom: '4px', margin: 0 }}>
                Plot Cancellation Slip
              </h1>
              <p style={{ fontSize: '14px', opacity: '0.95', margin: 0 }}>{societyName}</p>
              {societyLocation && (
                <p style={{ fontSize: '12px', opacity: '0.85', margin: '2px 0 0 0' }}>{societyLocation}</p>
              )}
            </div>
          </div>
          <div className='header-right' style={{
            textAlign: 'right',
            background: 'rgba(255,255,255,0.15)',
            padding: '12px 16px',
            borderRadius: '6px'
          }}>
            <p style={{ fontSize: '11px', opacity: '0.9', marginBottom: '4px', margin: 0 }}>
              Cancellation Date
            </p>
            <p style={{ fontSize: '14px', fontWeight: '600', margin: 0 }}>
              {formatDate(cancelPlot.createdAt)}
            </p>
          </div>
        </div>

        {/* Info Grid */}
        <div className='info-grid' style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '16px',
          marginBottom: '24px'
        }}>
          {/* Plot Information */}
          <div className='info-card' style={{
            background: '#f8f9fa',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            padding: '16px'
          }}>
            <div className='info-card-title' style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px',
              fontWeight: '600',
              color: '#0d9488',
              marginBottom: '12px',
              paddingBottom: '8px',
              borderBottom: '2px solid #0d9488'
            }}>
              <Building size={16} />
              Plot Information
            </div>
            <div className='info-item' style={{ marginBottom: '10px' }}>
              <div className='info-label' style={{
                fontSize: '10px',
                color: '#666',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '4px'
              }}>
                Plot Number
              </div>
              <div className='info-value' style={{
                fontSize: '13px',
                color: '#1a1a1a',
                fontWeight: '600'
              }}>
                {plot.plot_number || 'N/A'}
              </div>
            </div>
            <div className='info-item' style={{ marginBottom: '10px' }}>
              <div className='info-label' style={{
                fontSize: '10px',
                color: '#666',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '4px'
              }}>
                Block
              </div>
              <div className='info-value' style={{
                fontSize: '13px',
                color: '#1a1a1a',
                fontWeight: '600'
              }}>
                {plot.block || plot.block_number || 'N/A'}
              </div>
            </div>
            {plot.size && (
              <div className='info-item' style={{ marginBottom: '10px' }}>
                <div className='info-label' style={{
                  fontSize: '10px',
                  color: '#666',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '4px'
                }}>
                  Size
                </div>
                <div className='info-value' style={{
                  fontSize: '13px',
                  color: '#1a1a1a',
                  fontWeight: '600'
                }}>
                  {plot.size}
                </div>
              </div>
            )}
            {plot.marla && (
              <div className='info-item' style={{ marginBottom: '10px' }}>
                <div className='info-label' style={{
                  fontSize: '10px',
                  color: '#666',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '4px'
                }}>
                  Marla
                </div>
                <div className='info-value' style={{
                  fontSize: '13px',
                  color: '#1a1a1a',
                  fontWeight: '600'
                }}>
                  {plot.marla} Marla
                </div>
              </div>
            )}
            {plot.category && (
              <div className='info-item' style={{ marginBottom: '10px' }}>
                <div className='info-label' style={{
                  fontSize: '10px',
                  color: '#666',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '4px'
                }}>
                  Category
                </div>
                <div className='info-value' style={{
                  fontSize: '13px',
                  color: '#1a1a1a',
                  fontWeight: '600',
                  textTransform: 'capitalize'
                }}>
                  {plot.category}
                </div>
              </div>
            )}
            {plot.plot_type && (
              <div className='info-item'>
                <div className='info-label' style={{
                  fontSize: '10px',
                  color: '#666',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '4px'
                }}>
                  Plot Type
                </div>
                <div className='info-value' style={{
                  fontSize: '13px',
                  color: '#1a1a1a',
                  fontWeight: '600'
                }}>
                  {plot.plot_type}
                </div>
              </div>
            )}
          </div>

          {/* Customer Information */}
          <div className='info-card' style={{
            background: '#f8f9fa',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            padding: '16px'
          }}>
            <div className='info-card-title' style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px',
              fontWeight: '600',
              color: '#0d9488',
              marginBottom: '12px',
              paddingBottom: '8px',
              borderBottom: '2px solid #0d9488'
            }}>
              <User size={16} />
              Customer Information
            </div>
            <div className='info-item' style={{ marginBottom: '10px' }}>
              <div className='info-label' style={{
                fontSize: '10px',
                color: '#666',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '4px'
              }}>
                Name
              </div>
              <div className='info-value' style={{
                fontSize: '13px',
                color: '#1a1a1a',
                fontWeight: '600'
              }}>
                {customer.name || 'N/A'}
              </div>
            </div>
            <div className='info-item' style={{ marginBottom: '10px' }}>
              <div className='info-label' style={{
                fontSize: '10px',
                color: '#666',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '4px'
              }}>
                Phone
              </div>
              <div className='info-value' style={{
                fontSize: '13px',
                color: '#1a1a1a',
                fontWeight: '600'
              }}>
                {customer.contactInfo?.phone || 'N/A'}
              </div>
            </div>
            <div className='info-item'>
              <div className='info-label' style={{
                fontSize: '10px',
                color: '#666',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '4px'
              }}>
                CNIC
              </div>
              <div className='info-value' style={{
                fontSize: '13px',
                color: '#1a1a1a',
                fontWeight: '600'
              }}>
                {formatCNIC(customer.contactInfo?.cnic)}
              </div>
            </div>
          </div>

          {/* Cancellation Details */}
          <div className='info-card' style={{
            background: '#f8f9fa',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            padding: '16px'
          }}>
            <div className='info-card-title' style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px',
              fontWeight: '600',
              color: '#0d9488',
              marginBottom: '12px',
              paddingBottom: '8px',
              borderBottom: '2px solid #0d9488'
            }}>
              <FileText size={16} />
              Cancellation Details
            </div>
            <div className='info-item' style={{ marginBottom: '10px' }}>
              <div className='info-label' style={{
                fontSize: '10px',
                color: '#666',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '4px'
              }}>
                Refund Type
              </div>
              <div className='info-value' style={{
                fontSize: '13px',
                color: '#1a1a1a',
                fontWeight: '600',
                textTransform: 'capitalize'
              }}>
                {cancelPlot.refund_type || 'N/A'}
              </div>
            </div>
            <div className='info-item' style={{ marginBottom: '10px' }}>
              <div className='info-label' style={{
                fontSize: '10px',
                color: '#666',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '4px'
              }}>
                Status
              </div>
              <div className='info-value'>
                <span className={`status-badge ${cancelPlot.refund_completed ? 'status-completed' : 'status-pending'}`}
                  style={{
                    display: 'inline-block',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '11px',
                    fontWeight: '700',
                    textTransform: 'uppercase',
                    background: cancelPlot.refund_completed ? '#d1fae5' : '#fef3c7',
                    color: cancelPlot.refund_completed ? '#065f46' : '#92400e',
                    border: cancelPlot.refund_completed ? '1px solid #10b981' : '1px solid #f59e0b'
                  }}>
                  {cancelPlot.refund_completed ? 'COMPLETED' : 'PENDING'}
                </span>
              </div>
            </div>
            {cancelPlot.cancellation_reason && (
              <div className='info-item'>
                <div className='info-label' style={{
                  fontSize: '10px',
                  color: '#666',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '4px'
                }}>
                  Reason
                </div>
                <div className='info-value' style={{
                  fontSize: '13px',
                  color: '#1a1a1a',
                  fontWeight: '600'
                }}>
                  {cancelPlot.cancellation_reason}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Refund Breakdown */}
        <div className='refund-section' style={{
          background: '#f0fdfa',
          border: '2px solid #5eead4',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '24px'
        }}>
          <div className='refund-title' style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '15px',
            fontWeight: '700',
            color: '#0d9488',
            marginBottom: '16px',
            paddingBottom: '10px',
            borderBottom: '2px solid #0d9488'
          }}>
            <DollarSign size={18} />
            Refund Breakdown
          </div>
          <div className='refund-item' style={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: '10px 0',
            fontSize: '13px',
            borderBottom: '1px dashed #99f6e4'
          }}>
            <span className='refund-label' style={{ color: '#0f766e', fontWeight: '700', fontSize: '13px' }}>
              Total Amount Paid:
            </span>
            <span className='refund-value' style={{ fontWeight: '800', color: '#134e4a', fontSize: '15px' }}>
              {formatCurrency((cancelPlot.refunded_amount || 0) + (cancelPlot.remaining_balance || 0) + (cancelPlot.society_deduction_amount || 0) + (cancelPlot.dealer_commission_deducted || 0))}
            </span>
          </div>
          <div className='refund-item' style={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: '10px 0',
            fontSize: '13px',
            borderBottom: '1px dashed #99f6e4'
          }}>
            <span className='refund-label' style={{ color: '#0f766e', fontWeight: '600' }}>
              Total Refund Amount (Before Deductions):
            </span>
            <span className='refund-value' style={{ fontWeight: '700', color: '#134e4a' }}>
              {formatCurrency(cancelPlot.total_refund_amount || ((cancelPlot.refunded_amount || 0) + (cancelPlot.remaining_balance || 0) + (cancelPlot.society_deduction_amount || 0) + (cancelPlot.dealer_commission_deducted || 0)))}
            </span>
          </div>
          <div className='refund-item' style={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: '10px 0',
            fontSize: '13px',
            borderBottom: '1px dashed #99f6e4'
          }}>
            <span className='refund-label' style={{ color: '#0f766e', fontWeight: '600' }}>
              Dealer Commission:
            </span>
            <span className='refund-value refund-negative' style={{ fontWeight: '700', color: '#dc2626' }}>
              - {formatCurrency(cancelPlot.dealer_commission_deducted || 0)}
            </span>
          </div>
          <div className='refund-item' style={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: '10px 0',
            fontSize: '13px',
            borderBottom: '1px dashed #99f6e4'
          }}>
            <span className='refund-label' style={{ color: '#0f766e', fontWeight: '600' }}>
              Society Deduction ({cancelPlot.society_deduction_type === 'percentage' ? `${cancelPlot.society_deduction_percentage}%` : 'Fixed'}):
            </span>
            <span className='refund-value refund-negative' style={{ fontWeight: '700', color: '#dc2626' }}>
              - {formatCurrency(cancelPlot.society_deduction_amount)}
            </span>
          </div>
          <div className='refund-item' style={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: '10px 0',
            fontSize: '13px',
            borderBottom: '1px dashed #99f6e4'
          }}>
            <span className='refund-label' style={{ color: '#0f766e', fontWeight: '600' }}>
              Refunded Amount:
            </span>
            <span className='refund-value' style={{ fontWeight: '700', color: '#10b981' }}>
              {formatCurrency(cancelPlot.refunded_amount || 0)}
            </span>
          </div>
          {cancelPlot.remaining_balance > 0 && (
            <div className='refund-item' style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '10px 0',
              fontSize: '13px',
              borderBottom: '1px dashed #99f6e4'
            }}>
              <span className='refund-label' style={{ color: '#0f766e', fontWeight: '600' }}>
                Remaining Balance:
              </span>
              <span className='refund-value' style={{ fontWeight: '700', color: '#f59e0b' }}>
                {formatCurrency(cancelPlot.remaining_balance)}
              </span>
            </div>
          )}
          {cancelPlot.original_booking?.initial_payment && (
            <div className='refund-item' style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '10px 0',
              fontSize: '12px',
              borderBottom: '1px dashed #99f6e4',
              opacity: 0.8
            }}>
              <span className='refund-label' style={{ color: '#0f766e', fontWeight: '500' }}>
                Initial Payment (at booking):
              </span>
              <span className='refund-value' style={{ fontWeight: '600', color: '#134e4a' }}>
                {formatCurrency(cancelPlot.original_booking.initial_payment)}
              </span>
            </div>
          )}
          <div className='refund-item' style={{
            display: 'flex',
            justifyContent: 'space-between',
            borderBottom: 'none',
            borderTop: '3px solid #0d9488',
            paddingTop: '14px',
            marginTop: '10px',
            fontWeight: '800',
            fontSize: '16px',
            color: '#0d9488',
            background: 'rgba(13, 148, 136, 0.05)',
            padding: '14px 12px',
            borderRadius: '6px'
          }}>
            <span className='refund-label' style={{ fontSize: '14px' }}>Net Refund Amount:</span>
            <span className='refund-value' style={{ fontSize: '18px' }}>{formatCurrency((cancelPlot.refunded_amount || 0) + (cancelPlot.remaining_balance || 0))}</span>
          </div>
        </div>

        {/* Installments Table - Only for installment refunds */}
        {cancelPlot.refund_type === 'installment' && refundInstallments.length > 0 && (
          <div style={{ marginTop: '24px' }}>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => {
              const pageStartIndex = (pageNum - 1) * itemsPerPage;
              const pageEndIndex = pageStartIndex + itemsPerPage;
              const pageInstallments = refundInstallments.slice(pageStartIndex, pageEndIndex);
              
              return (
                <div key={pageNum} style={{ pageBreakAfter: pageNum < totalPages ? 'always' : 'auto', marginBottom: pageNum < totalPages ? '30px' : '0' }}>
                  <div style={{ background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)', color: 'white', padding: '12px 16px', borderRadius: '8px 8px 0 0' }}>
                    <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '700' }}>Refund Installments Schedule</h3>
                  </div>
                  <table className='installments-table' style={{ width: '100%', borderCollapse: 'collapse', margin: '0 0 20px 0', background: 'white', borderRadius: '0 0 8px 8px', overflow: 'hidden', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                    <thead style={{ background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)', color: 'white' }}>
                      <tr>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>#</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Amount</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Due Date</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Status</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Payment Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pageInstallments.map((inst, idx) => {
                        const installmentNum = pageStartIndex + idx + 1;
                        const isPaid = inst.status === 'paid' || inst.is_paid;
                        return (
                          <tr key={inst._id || idx} style={{ borderBottom: idx < pageInstallments.length - 1 ? '1px solid #e5e7eb' : 'none' }}>
                            <td style={{ padding: '10px 12px', fontSize: '12px', fontWeight: '600' }}>{installmentNum}</td>
                            <td style={{ padding: '10px 12px', fontSize: '12px', fontWeight: '700', color: '#0d9488' }}>{formatCurrency(inst.amount)}</td>
                            <td style={{ padding: '10px 12px', fontSize: '12px' }}>{formatDate(inst.due_date)}</td>
                            <td style={{ padding: '10px 12px', fontSize: '12px' }}>
                              <span style={{
                                background: isPaid ? '#d1fae5' : '#fef3c7',
                                color: isPaid ? '#065f46' : '#92400e',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '10px',
                                fontWeight: '600'
                              }}>
                                {isPaid ? 'Paid' : 'Pending'}
                              </span>
                            </td>
                            <td style={{ padding: '10px 12px', fontSize: '12px' }}>{inst.payment_date ? formatDate(inst.payment_date) : '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div style={{ textAlign: 'center', fontSize: '11px', color: '#6b7280', marginTop: '10px', padding: '8px', background: '#f9fafb', borderRadius: '4px' }}>
                    Page {pageNum} of {totalPages} | Showing {pageInstallments.length} of {refundInstallments.length} installments
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Signature Section */}
        <div className='signature-section' style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '20px',
          marginBottom: '24px'
        }}>
          <div className='signature-box' style={{
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            padding: '16px',
            textAlign: 'center',
            background: '#fafafa'
          }}>
            <div className='signature-label' style={{
              fontSize: '12px',
              fontWeight: '600',
              color: '#0d9488',
              marginBottom: '50px'
            }}>
              Customer Signature
            </div>
            <div className='signature-line' style={{
              borderTop: '2px solid #0d9488',
              marginBottom: '8px'
            }}></div>
            <div className='signature-name' style={{
              fontSize: '11px',
              color: '#666'
            }}>
              {customer.name || 'Customer Name'}
            </div>
          </div>
          <div className='signature-box' style={{
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            padding: '16px',
            textAlign: 'center',
            background: '#fafafa'
          }}>
            <div className='signature-label' style={{
              fontSize: '12px',
              fontWeight: '600',
              color: '#0d9488',
              marginBottom: '50px'
            }}>
              Authorized Signature
            </div>
            <div className='signature-line' style={{
              borderTop: '2px solid #0d9488',
              marginBottom: '8px'
            }}></div>
            <div className='signature-name' style={{
              fontSize: '11px',
              color: '#666'
            }}>
              {societyName}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className='footer' style={{
          textAlign: 'center',
          fontSize: '10px',
          color: '#666',
          borderTop: '1px solid #e0e0e0',
          paddingTop: '16px'
        }}>
          <p style={{ margin: '4px 0' }}>
            This is a computer-generated document. No signature is required.
          </p>
          <p style={{ margin: '4px 0' }}>
            Generated on {new Date().toLocaleString('en-US', { dateStyle: 'long', timeStyle: 'short' })}
          </p>
        </div>
      </div>

      {/* Preview Installments Table - Only for installment refunds */}
      {cancelPlot.refund_type === 'installment' && refundInstallments.length > 0 && (
        <div className='no-print mb-6 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6'>
          <h3 className='text-lg font-semibold text-slate-800 dark:text-white mb-4 flex items-center gap-2'>
            <FileText size={20} className='text-teal-500' />
            Refund Installments Preview (Page {currentPage} of {totalPages})
          </h3>
          {loadingInstallments ? (
            <div className='text-center py-8 text-slate-500'>Loading installments...</div>
          ) : (
            <>
              <div className='overflow-x-auto'>
                <table className='w-full text-sm border-collapse'>
                  <thead className='bg-gradient-to-r from-teal-50 to-teal-100 dark:from-teal-900/30 dark:to-teal-800/30'>
                    <tr>
                      <th className='px-4 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase'>#</th>
                      <th className='px-4 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase'>Amount</th>
                      <th className='px-4 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase'>Due Date</th>
                      <th className='px-4 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase'>Status</th>
                      <th className='px-4 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase'>Payment Date</th>
                    </tr>
                  </thead>
                  <tbody className='divide-y divide-slate-200 dark:divide-slate-700'>
                    {currentInstallments.map((inst, idx) => {
                      const installmentNum = startIndex + idx + 1;
                      const isPaid = inst.status === 'paid' || inst.is_paid;
                      return (
                        <tr key={inst._id || idx} className={`hover:bg-slate-50 dark:hover:bg-slate-700/50 ${
                          isPaid ? 'bg-emerald-50/30 dark:bg-emerald-900/10' : ''
                        }`}>
                          <td className='px-4 py-3 font-medium text-slate-900 dark:text-white'>{installmentNum}</td>
                          <td className='px-4 py-3 font-bold text-teal-600 dark:text-teal-400'>{formatCurrency(inst.amount)}</td>
                          <td className='px-4 py-3 text-slate-700 dark:text-slate-300'>{formatDate(inst.due_date)}</td>
                          <td className='px-4 py-3'>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                              isPaid 
                                ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300' 
                                : 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300'
                            }`}>
                              {isPaid ? 'Paid' : 'Pending'}
                            </span>
                          </td>
                          <td className='px-4 py-3 text-slate-600 dark:text-slate-400'>
                            {inst.payment_date ? formatDate(inst.payment_date) : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 && (
                <div className='mt-4 flex items-center justify-between text-sm text-slate-600 dark:text-slate-400'>
                  <span>Showing {startIndex + 1}-{Math.min(endIndex, refundInstallments.length)} of {refundInstallments.length} installments</span>
                  <div className='flex items-center gap-2'>
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className='px-3 py-1 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded hover:bg-slate-50 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
                    >
                      Previous
                    </button>
                    <span className='px-3 font-medium'>Page {currentPage} of {totalPages}</span>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className='px-3 py-1 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded hover:bg-slate-50 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      <button 
        className='no-print mt-6 w-full bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 text-white py-3 px-6 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 active:scale-95' 
        onClick={handlePrint}
      >
        <Printer size={20} />
        Print Cancellation Slip{cancelPlot.refund_type === 'installment' && refundInstallments.length > 0 ? ` (${totalPages} ${totalPages > 1 ? 'pages' : 'page'})` : ''}
      </button>
    </>
  );
};

export default CancelPlotSlip;